// Compare: the assistant beside the shop's own look. Left, the shop's home page as the read saw it; right,
// the widget as the box renders it; under them the colours the shop uses and the colours of the assistant,
// each with whether it is the shop's colour. A colour that is close to one of the shop's but is not it
// can be fixed with Chat, which drafts the request with both colours named.
import { useState } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { Segmented, StatusPill } from '@/components/ui/kit';
import { EmptyState, ErrorState } from '../primitives';
import { WorkspaceIcon } from '../icons';
import type { BrandComparison, StoreBrand, StoreDetail } from './api';
import { useStorefrontApi } from './api';
import { when } from './format';
import { useRemote } from './use-storefront';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;
type Device = 'computer' | 'phone';

// Where a colour came from, as the evidence names it, in the person's words.
const SOURCE_COPY: Record<string, StorefrontCopyKey> = {
  logo: 'lookFromLogo',
  'theme-color': 'lookFromTheme',
  'tile-color': 'lookFromTile',
  'mask-icon': 'lookFromMask',
  manifest: 'lookFromManifest',
  header: 'lookFromHeader',
  button: 'lookFromButton',
  variable: 'lookFromVariable',
};
const sourceWord = (copy: Copy, source: string) => copy[SOURCE_COPY[source] ?? 'lookFromVariable'];

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** The colours the shop's pages name, once each with where it came from (at most eight). */
export function shopColours(brand: StoreBrand): { hex: string; source: string }[] {
  const meta = brand.meta;
  const found: [string | null | undefined, string][] = [
    ...(brand.logo?.colours ?? []).map((colour): [string, string] => [colour.hex, 'logo']),
    ...(meta?.themeColor ?? []).map((item): [string | null, string] => [
      item.content,
      'theme-color',
    ]),
    [meta?.tileColor, 'tile-color'],
    [meta?.maskIcon?.color, 'mask-icon'],
    [meta?.manifest?.themeColor, 'manifest'],
  ];
  const seen = new Set<string>();
  const colours: { hex: string; source: string }[] = [];
  for (const [value, source] of found) {
    if (!value || !HEX.test(value)) continue;
    const hex = value.toUpperCase();
    if (seen.has(`${hex}|${source}`)) continue;
    seen.add(`${hex}|${source}`);
    colours.push({ hex, source });
  }
  return colours.slice(0, 8);
}

function Swatch({ hex, copy }: { hex: string; copy: Copy }) {
  const safe = HEX.test(hex);
  return (
    <span
      className="sf-look-swatch"
      role="img"
      aria-label={formatCopy(copy.lookSwatch, { colour: hex })}
      style={safe ? { background: hex } : undefined}
    />
  );
}

function verdictText(copy: Copy, role: BrandComparison['roles'][number]): string {
  if (role.verdict === 'other' || !role.nearest) return copy.lookOwn;
  const words = {
    colour: role.nearest.hex,
    source: sourceWord(copy, role.nearest.source),
    delta: role.nearest.deltaE.toFixed(1),
  };
  return formatCopy(role.verdict === 'match' ? copy.lookSame : copy.lookOff, words);
}

function Picture({ image, alt }: { image: { mediaType: string; data: string }; alt: string }) {
  return <img src={`data:${image.mediaType};base64,${image.data}`} alt={alt} loading="lazy" />;
}

export function LookCompare({
  store,
  version,
  onFix,
}: {
  store: StoreDetail;
  version: number;
  onFix?: (prompt: string) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const api = useStorefrontApi();
  const [device, setDevice] = useState<Device>('computer');
  const brand = store.brand ?? null;
  const remote = useRemote(
    brand ? () => api.lookCompare(store.id, device) : null,
    `${store.id}:${version}:${device}`,
  );
  if (!brand)
    return (
      <div data-testid="sf-look-none">
        <EmptyState icon="store" title={copy.lookNoneTitle} description={copy.lookNoneBody} />
      </div>
    );
  const answer = remote.data;
  const roleWord = (role: 'header' | 'brand') =>
    role === 'header' ? copy.lookRoleHeader : copy.lookRoleBrand;
  return (
    <div className="sf-look" data-testid="sf-look">
      <div className="sf-look-bar">
        <Segmented
          label={copy.lookDeviceLabel}
          options={[
            { value: 'computer', label: copy.deviceComputer },
            { value: 'phone', label: copy.devicePhone },
          ]}
          value={device}
          onChange={(next) => setDevice(next as Device)}
        />
      </div>
      {remote.error && !answer ? (
        <ErrorState message={copy.lookFailed} onRetry={remote.refresh} />
      ) : !answer ? (
        <p className="sf-look-loading" role="status" data-testid="sf-look-loading">
          {copy.lookLoading}
        </p>
      ) : (
        <>
          {answer.neutral && (
            <p className="sf-look-neutral" data-testid="sf-look-neutral">
              {copy.lookNeutral}
            </p>
          )}
          <div className="sf-look-pair">
            <figure className="sf-look-figure" data-testid="sf-look-shop">
              <figcaption>
                <b>{copy.lookShop}</b>
                {answer.shop?.at && (
                  <span>
                    {formatCopy(copy.lookShopCaption, { when: when(answer.shop.at, locale) })}
                  </span>
                )}
              </figcaption>
              {answer.shop ? (
                <Picture image={answer.shop.image} alt={copy.lookShop} />
              ) : (
                <p className="sf-look-missing">{copy.lookNoShopPicture}</p>
              )}
            </figure>
            <figure className="sf-look-figure" data-testid="sf-look-widget">
              <figcaption>
                <b>{copy.lookWidget}</b>
                <span>
                  {formatCopy(copy.lookWidgetCaption, { version: String(answer.widget.version) })}
                </span>
              </figcaption>
              <Picture image={answer.widget.screenshot} alt={copy.lookWidget} />
            </figure>
          </div>
          <section className="sf-look-colours" aria-label={copy.lookColoursShop}>
            <h3>{copy.lookColoursShop}</h3>
            <ul>
              {shopColours(brand).map((colour) => (
                <li key={`${colour.hex}|${colour.source}`}>
                  <Swatch hex={colour.hex} copy={copy} />
                  <code>{colour.hex}</code>
                  <span>{sourceWord(copy, colour.source)}</span>
                </li>
              ))}
            </ul>
          </section>
          {answer.compared && (
            <section className="sf-look-colours" aria-label={copy.lookColoursAssistant}>
              <h3>{copy.lookColoursAssistant}</h3>
              <ul data-testid="sf-look-roles">
                {answer.compared.roles.map((role) => (
                  <li key={role.role} data-verdict={role.verdict}>
                    <Swatch hex={role.rendered} copy={copy} />
                    <b>{roleWord(role.role)}</b>
                    <code>{role.rendered}</code>
                    <StatusPill
                      tone={
                        role.verdict === 'match'
                          ? 'success'
                          : role.verdict === 'off'
                            ? 'warning'
                            : 'neutral'
                      }
                    >
                      {verdictText(copy, role)}
                    </StatusPill>
                  </li>
                ))}
              </ul>
              {onFix && answer.compared.off.length > 0 && (
                <button
                  type="button"
                  className="ws-button"
                  data-testid="sf-look-fix"
                  onClick={() => {
                    const off = answer.compared!.off[0];
                    onFix(
                      formatCopy(copy.lookFixPrompt, {
                        role: roleWord(off.role).toLowerCase(),
                        colour: off.rendered,
                        exact: off.evidence,
                        source: sourceWord(copy, off.source),
                      }),
                    );
                  }}
                >
                  <WorkspaceIcon name="chat" size={14} />
                  {copy.lookFix}
                </button>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
